/**
 * Section 4: Property Condition + Details
 */

import { useCallback } from 'preact/hooks';
import { useForm } from '@ox/visits-ui/context/FormContext';
import { Select, Textarea } from '@ox/visits-ui/components/shared/FormComponents';
import { FileUpload } from '@ox/visits-ui/components/shared/FileUpload';

interface Section4InteriorConditionProps {
  isStandard?: boolean;
}

export function Section4InteriorCondition({ isStandard }: Section4InteriorConditionProps) {
  const { formData, updateField } = useForm();

  const handleCleanlinessChange = useCallback((value: string) => {
    updateField('cleanliness', value);
  }, [updateField]);

  const handleCleanlinessDescriptionChange = useCallback((value: string) => {
    updateField('cleanliness_description', value);
  }, [updateField]);

  const handleOdorsPresentChange = useCallback((value: string) => {
    updateField('odors_present', value);
  }, [updateField]);

  const handleOdorsDescriptionChange = useCallback((value: string) => {
    updateField('odors_description', value);
  }, [updateField]);

  const handleInteriorDamageChange = useCallback((value: string) => {
    updateField('interior_damage', value);
  }, [updateField]);

  const handleWaterLeakChange = useCallback((value: string) => {
    updateField('water_leak', value);
  }, [updateField]);

  const handleWaterLeakDescriptionChange = useCallback((value: string) => {
    updateField('water_leak_description', value);
  }, [updateField]);

  return (
    <div className="form-section">
      <h2>Interior Condition</h2>

      <Select
        label={`How would you rate the home's current cleanliness? "Good" means it is listing ready and marketable.`}
        name="cleanliness"
        value={
          formData.cleanliness || ''
        }
        onChange={handleCleanlinessChange}
        options={[
          { label: 'Select...', value: '', disabled: true },
          { label: 'Good', value: 'Good' },
          { label: 'Poor', value: 'Poor' },
        ]}
        required
      />
      {!isStandard && formData.cleanliness === 'Poor' && (
        <Textarea
          label="What should be cleaned?"
          name="cleanliness_description"
          value={formData.cleanliness_description || ''}
          onChange={handleCleanlinessDescriptionChange}
          autoSize
          required
        />
      )}
      {isStandard && formData.cleanliness === 'Poor' && (
        <>
          <Textarea
            label="What needs to be cleaned?"
            name="cleanliness_description"
            value={formData.cleanliness_description || ''}
            onChange={handleCleanlinessDescriptionChange}
            autoSize
            required
          />
          <FileUpload
            label="Photo of the cleanliness issue(s)"
            mediaType="photo_cleanliness"
            sectionNumber={5}
            minFiles={1}
            maxFiles={10}
            accept="image/*"
            helpText="Capture what needs to be cleaned so the issue is clearly documented."
            required
          />
        </>
      )}

      {isStandard && (
        <>
          <Select
            label="Any Odors Present?"
            name="odors_present"
            value={
              formData.odors_present || ''
            }
            onChange={handleOdorsPresentChange}
            options={[
              { label: 'Select...', value: '', disabled: true },
              { label: 'Yes - pet odor in living room', value: 'Yes' },
              { label: 'No', value: 'No' },
            ]}
            required
          />
          {formData.odors_present === 'Yes' && (
            <Textarea
              label="Describe the odors present"
              name="odors_description"
              value={formData.odors_description || ''}
              onChange={handleOdorsDescriptionChange}
              placeholder="Describe the odors present"
              autoSize
              required
            />
          )}

          <Select
            label="Are there signs of a water leak?"
            name="water_leak"
            value={formData.water_leak || ''}
            onChange={handleWaterLeakChange}
            options={[
              { label: 'Select...', value: '', disabled: true },
              { label: 'Yes', value: 'Yes' },
              { label: 'No', value: 'No' },
            ]}
            required
          />
          {formData.water_leak === 'Yes' && (
            <>
              <Textarea
                label="Describe the water leak (location and what you observed)"
                name="water_leak_description"
                value={formData.water_leak_description || ''}
                onChange={handleWaterLeakDescriptionChange}
                autoSize
                required
              />
              <FileUpload
                label="Photo of the water leak / suspected source"
                mediaType="photo_water_leak"
                sectionNumber={5}
                minFiles={1}
                maxFiles={10}
                accept="image/*"
                helpText="Capture the leak location and any resulting damage (e.g. staining, standing water, warped flooring)."
                required
              />
            </>
          )}
        </>
      )}

      <Textarea
        label="Is there any specific interior conditions/detractors to the home that you would you like to note?"
        name="interior_damage"
        value={formData.interior_damage || ''}
        onChange={handleInteriorDamageChange}
        placeholder="Please provide location of damage with description. (ex. Mold found under first floor bathroom sink, photos to be provided in the next question)"
        maxLength={1000}
        autoSize
      />
  
      <FileUpload
        label={`Interior Damage Photos (Up to ${isStandard ? 10 : 5} Photos)`}
        mediaType="photos_interior_damage"
        sectionNumber={5}
        maxFiles={isStandard ? 10 : 5}
        accept="image/*"
      />
    </div>
  );
}
